import { computed, Ref, ref } from "@vue/composition-api";
import { clone, identity, flat_by, indexBy } from "@/fp";

export const UsePendingCollection = <T>(props: {
  seed: T[];
  check_change_by_prop: keyof T;
  create_check_change_prop?: (t: T) => string;
  flat_by?: keyof T;
  uniq_prop: keyof T;
}) => {
  const {
    check_change_by_prop,
    create_check_change_prop,
    seed,
    flat_by: flat_by_prop,
    uniq_prop,
  } = props;
  const COMBINATION_CHECK_CHANCE_PROP = "combination_check_prop";
  const initial_state: Ref<T[]> = ref(clone(seed, true));
  const _pending_items: Ref<T[]> = ref(clone(seed, true));
  const pending_items = computed(() => _pending_items.value);

  let initial_state_indexed: any = {};
  const diff_obj = ref({});
  const _has_pending_changes = ref(false);
  const has_pending_changes = computed(() => _has_pending_changes.value);
  //const selected_items = computed(()=> )

  const run_change_check = () => {
    console.log("has_pending_changes : ", diff_obj.value);
    if (create_check_change_prop) {
      _has_pending_changes.value = Object.entries(diff_obj.value).some(
        ([k, v]) => create_check_change_prop(initial_state_indexed[k]) != v
      );
    } else {
      _has_pending_changes.value = Object.entries(diff_obj.value).some(
        ([k, v]) => initial_state_indexed[k][check_change_by_prop] != v
      );
    }
  };

  const update_item = (item: T) => {
    console.log("update_item", item);
    _pending_items.value = _pending_items.value.map((l) => {
      if (l[uniq_prop] === item[uniq_prop]) {
        return item;
      }
      return l;
    });
    if (create_check_change_prop) {
      // @ts-ignore
      diff_obj.value[item[uniq_prop]] = create_check_change_prop(item);
    } else {
      // @ts-ignore
      diff_obj.value[item[uniq_prop]] = item[check_change_by_prop];
    }
    run_change_check();
  };

  const discard_changes = () => {
    _pending_items.value = clone(initial_state.value, true);
    _has_pending_changes.value = false;
  };

  const commit_changes = () => {
    initial_state.value = clone(_pending_items.value, true);
    index_initial_state();
    _has_pending_changes.value = false;
  };

  const get_initial_state = () => clone(initial_state.value, true);

  const index_initial_state = () => {
    if (flat_by_prop) {
      const flat_collection = flat_by<T, keyof T>(
        flat_by_prop,
        identity
      )(initial_state.value).map((l) => {
        if (create_check_change_prop) {
          return {
            [uniq_prop]: l[uniq_prop],
            [COMBINATION_CHECK_CHANCE_PROP]: create_check_change_prop(l),
          };
        } else {
          return {
            [uniq_prop]: l[uniq_prop],
            [check_change_by_prop]: l[check_change_by_prop],
          };
        }
      });
      // @ts-ignore
      initial_state_indexed = indexBy(uniq_prop)(flat_collection);
    } else {
      // @ts-ignore
      initial_state_indexed = indexBy(uniq_prop)(initial_state.value);
    }
  };

  const get_indexed_pending_items = () => {
    if (flat_by_prop) {
      const flat_collection = flat_by<T, keyof T>(
        flat_by_prop,
        identity
      )(clone(_pending_items.value, true));
      // @ts-ignore
      return indexBy(uniq_prop)(flat_collection);
    } else {
      // @ts-ignore
      return indexBy(uniq_prop)(clone(pending_items.value, true));
    }
  };

  const get_pending_diff = (): T[] => {
    const diff_arr: T[] = [];
    if (create_check_change_prop) {
      Object.entries(diff_obj.value).forEach(([k, v]) => {
        if (create_check_change_prop(initial_state_indexed[k]) != v) {
          const changed_item = pending_items.value.find(
            // @ts-ignore
            (d) => d[uniq_prop] === k
          ) as T;

          diff_arr.push(changed_item);
        }
      });
    } else {
      Object.entries(diff_obj.value).forEach(([k, v]) => {
        if (initial_state_indexed[k][check_change_by_prop] != v) {
          const changed_item = pending_items.value.find(
            // @ts-ignore
            (d) => d[uniq_prop] === k
          ) as T;
          diff_arr.push(changed_item);
        }
      });
    }
    return diff_arr;
  };

  const init = (seed: T[]) => {
    _pending_items.value = clone(seed, true);
    initial_state.value = clone(seed, true);
    index_initial_state();
  };

  init(seed);

  return {
    init,
    pending_items,
    get_indexed_pending_items,
    commit_changes,
    discard_changes,
    get_initial_state,
    has_pending_changes,
    update_item,
    get_pending_diff,
  };
};

// create function to check if

export const UsePendingObject = <T>(
  seed: Record<string, T>,
  test_eq: (t1: T, t2: T) => boolean
) => {
  const _initial_state: Ref<Record<string, T>> = ref(clone(seed, true));

  const _pending_items: Ref<Record<string, T>> = ref(clone(seed, true));
  const pending_items = computed(() => _pending_items.value);

  const _has_pending_changes = ref(false);
  const has_pending_changes = computed(() => _has_pending_changes.value);

  const update_item = (id: string, val: T) => {
    _pending_items.value = {
      ..._pending_items.value,
      [id]: val,
    };
    _has_pending_changes.value = Object.entries(_pending_items.value).some(
      ([k, v]) => !test_eq(v, _initial_state.value[k])
    );
  };

  const discard_changes = () => {
    _pending_items.value = clone(_initial_state.value, true);
    _has_pending_changes.value = false;
  };

  const commit_changes = () => {
    _initial_state.value = clone(_pending_items.value, true);
    _has_pending_changes.value = false;
  };

  const get_initial_state = () => clone(_initial_state.value, true);

  const init = (seed: Record<string, T>) => {
    _pending_items.value = clone(seed, true);
    _initial_state.value = clone(seed, true);
  };

  init(seed);

  return {
    init,
    pending_items,
    commit_changes,
    discard_changes,
    get_initial_state,
    has_pending_changes,
    update_item,
  };
};
